import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {fetchStorages} from 'api/documents/create';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
// import {ReactComponent as TrashIcon} from 'assets/svg/trash.svg';
import {ReactComponent as WarningIcon} from 'assets/svg/warning.svg';
import {BackTo} from 'components/common/BackTo';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {
    boundUserWithParticipantFx,
    editUserData$,
    removeUserFx,
    updateUserFx,
} from 'models/profile/staff/table';
import {EmployeeRegistrationRequest, EmployeeUpdateRequest} from 'types';

import {
    Actions,
    BackButton,
    CancelConfirmButton,
    ConfirmDeleteButton,
    ConfirmRemoveFooter,
    ConfirmRemoveHeader,
    ConfirmRemoveHeaderIcon,
    ConfirmRemoveModalContent,
    ConfirmRemoveTitle,
    CustomTitle,
    // DeleteButton,
    Field,
    FormFields,
    ModalCloseButton,
    RemoveConfirmationModal,
    SubmitButton,
    WarningMessage,
    Wrapper,
} from './styled';

type FormField = {
    name: keyof EmployeeRegistrationRequest;
    readonly?: boolean;
    options?: SelectOption<string>[];
};

type UpdateUserForm = Partial<EmployeeRegistrationRequest> & {
    first_name: string;
    last_name: string;
    middle_name?: string;
    phone?: string;
    storageLocationId?: number | null;
};

type StorageLocation = {
    id: number;
    storageName: string | null;
};

export const UpdateUser = () => {
    const {t, i18n} = useTranslation();
    const permissions = useStore(permissions$);
    const [storageLocations, setStorageLocations] = useState<StorageLocation[]>([]);
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    const translateValidates = useCallback(
        (key: string) => t(`pages.register.validations.${key}`),
        [i18n.language],
    );
    const translateForm = useCallback((key: string) => t('pages.register.fields.' + key), [
        i18n.language,
    ]);

    const editUserData = useStore(editUserData$);

    const schema = useMemo(
        () =>
            yup.object().shape({
                first_name: yup.string().required(translateValidates('required')),
                middle_name: yup.string().notRequired(),
                last_name: yup.string().required(translateValidates('required')),
                phone: yup
                    .string()
                    .transform((value) => (value === '' ? null : value))
                    .nullable()
                    .notRequired()
                    .matches(/^\+?\d{10,15}$/, translateValidates('phone')),
                storageLocationId: yup
                    .number()
                    .nullable()
                    .notRequired(),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors, isDirty},
        handleSubmit,
        reset,
    } = useForm<UpdateUserForm>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    useEffect(() => {
        if (!editUserData) {
            history.push(ROUTE.STAFF);
            return;
        }

        reset({
            first_name: editUserData.firstName,
            middle_name: editUserData.middleName,
            last_name: editUserData.lastName,
            phone: editUserData.phone,
            storageLocationId: editUserData.clientId ?? null,
        });
    }, [editUserData, reset]);

    useEffect(() => {
        fetchStorages()
            .then((response) => setStorageLocations(response.storageLocations))
            .catch((error) => console.error(error));
    }, []);

    const storageLocationOptions = useMemo<SelectOption<number>[]>(
        () =>
            storageLocations.map((item) => ({
                value: item.id,
                label: item.storageName ?? '-',
            })),
        [storageLocations],
    );

    const canEditUser =
        permissions.includes(permissions.userOwnEdit) ||
        permissions.includes(permissions.userAllEdit);

    const onSubmit = async (data: UpdateUserForm) => {
        try {
            if (!editUserData?.id) return;
            if (!canEditUser) return;

            const payload: EmployeeUpdateRequest & {id: string | number} = {
                firstName: {value: data.first_name, reset: false},
                middleName: {value: data.middle_name || '', reset: false},
                lastName: {value: data.last_name, reset: false},
                phone: {value: data.phone || '', reset: false},
                id: editUserData.id,
            };

            await updateUserFx(payload);
            if ((editUserData.clientId ?? null) !== (data.storageLocationId ?? null)) {
                await boundUserWithParticipantFx({
                    userId: editUserData.id,
                    clientId: data.storageLocationId ?? null,
                });
            }
            reset();
            history.push(ROUTE.STAFF);
        } catch (error) {
            console.log(error);
        }
    };

    const onRemoveUser = async () => {
        if (!editUserData?.id) return;
        if (!canEditUser) return;

        await removeUserFx(editUserData.id);
        setShowRemoveConfirmation(false);
        reset();
        history.push(ROUTE.STAFF);
    };

    const formFields: FormField[] = useMemo(
        () => [{name: 'first_name'}, {name: 'middle_name'}, {name: 'last_name'}, {name: 'phone'}],
        [t],
    );

    const controlRender = (
        controlEvents: ControllerRenderProps<UpdateUserForm>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
        };

        const props = {...baseProps, ...controlEvents};
        return <Input {...props} />;
    };

    // const canRemoveUser = !!editUserData && canEditUser;

    return (
        <>
            <BackTo to={ROUTE.STAFF}>{t('pages.profile.staff.create.back')}</BackTo>
            <ContentContainer>
                <Wrapper>
                    <form onSubmit={handleSubmit(onSubmit)} style={{marginTop: 50}}>
                        <CustomTitle size={4}>{t('pages.profile.staff.update.title')}</CustomTitle>
                        <FormFields>
                            {formFields.map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>{t(`pages.register.fields.${field.name}`)}</span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                            <Field>
                                <span style={{marginBottom: 2}}>
                                    {t('pages.profile.staff.storageLocation')}
                                </span>
                                <Controller
                                    name="storageLocationId"
                                    control={control}
                                    defaultValue={null}
                                    render={({value, onChange}) => (
                                        <Select
                                            isClearable
                                            value={
                                                storageLocationOptions.find(
                                                    (option) => option.value === value,
                                                ) ?? null
                                            }
                                            options={storageLocationOptions}
                                            onChange={(option: SelectOption<number> | null) =>
                                                onChange(option?.value ?? null)
                                            }
                                        />
                                    )}
                                />
                            </Field>
                        </FormFields>
                        <Actions>
                            {/*<DeleteButton*/}
                            {/*    type="button"*/}
                            {/*    size="sm"*/}
                            {/*    variant={ButtonVariant.Outline}*/}
                            {/*    disabled={!canRemoveUser}*/}
                            {/*    $alignLeft*/}
                            {/*    icon={*/}
                            {/*        <Icon source={TrashIcon} fillColor="currentColor" width={16} />*/}
                            {/*    }*/}
                            {/*    onClick={() => setShowRemoveConfirmation(true)}*/}
                            {/*>*/}
                            {/*    {t('pages.profile.staff.confirm.ok')}*/}
                            {/*</DeleteButton>*/}
                            <BackButton
                                type="button"
                                size="sm"
                                variant={ButtonVariant.Outline}
                                onClick={() => {
                                    history.push(ROUTE.STAFF);
                                    reset();
                                }}
                            >
                                {t('pages.profile.staff.create.cancel')}
                            </BackButton>
                            <SubmitButton
                                type="submit"
                                size="sm"
                                disabled={!isDirty || !canEditUser}
                            >
                                {t('pages.profile.staff.update.updateBtn')}
                            </SubmitButton>
                        </Actions>
                    </form>
                </Wrapper>
            </ContentContainer>
            <RemoveConfirmationModal
                modalWidth={0}
                show={showRemoveConfirmation}
                hideCloseButton
                onClose={() => setShowRemoveConfirmation(false)}
            >
                <ConfirmRemoveModalContent>
                    <ConfirmRemoveHeader>
                        <ConfirmRemoveTitle>
                            <ConfirmRemoveHeaderIcon>
                                <Icon
                                    source={WarningIcon}
                                    strokeColor="#FF3030"
                                    fillColor="transparent"
                                    width={20}
                                    height={20}
                                />
                            </ConfirmRemoveHeaderIcon>
                            {t('pages.profile.staff.confirm.title')}
                        </ConfirmRemoveTitle>
                        <ModalCloseButton
                            type="button"
                            onClick={() => setShowRemoveConfirmation(false)}
                        >
                            <Icon source={CrossIcon} fillColor="#939AA4" width={12} height={12} />
                        </ModalCloseButton>
                    </ConfirmRemoveHeader>
                    <WarningMessage>
                        <p>{t('pages.profile.staff.confirm.deleteWarning')}</p>
                        <p>{t('pages.profile.staff.confirm.deleteWarningUndo')}</p>
                    </WarningMessage>
                </ConfirmRemoveModalContent>
                <ConfirmRemoveFooter>
                    <CancelConfirmButton
                        size="sm"
                        variant={ButtonVariant.Outline}
                        onClick={() => setShowRemoveConfirmation(false)}
                    >
                        {t('pages.profile.staff.confirm.cancel')}
                    </CancelConfirmButton>
                    <ConfirmDeleteButton size="sm" onClick={onRemoveUser}>
                        {t('pages.profile.staff.confirm.delete')}
                    </ConfirmDeleteButton>
                </ConfirmRemoveFooter>
            </RemoveConfirmationModal>
        </>
    );
};
